import { Component, input, output, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-radio',
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-2">
      @if (label()) {
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <div [class]="containerClasses" [attr.role]="'radiogroup'" [attr.aria-labelledby]="labelId">
        @for (option of options(); track option.value) {
          <label 
            [for]="optionId(option.value)"
            [class]="getOptionLabelClasses(option)"
            [attr.aria-disabled]="option.disabled || disabled()">
            <input
              type="radio"
              [id]="optionId(option.value)"
              [name]="name()"
              [value]="option.value"
              [checked]="isSelected(option.value)"
              [disabled]="option.disabled || disabled()"
              [class]="radioClasses"
              (change)="selectOption(option.value)"
              [attr.aria-checked]="isSelected(option.value)" />
            @if (option.icon) {
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="option.icon" />
              </svg>
            }
            <span>{{ option.label }}</span>
          </label>
        }
      </div>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium">
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class RadioComponent implements ControlValueAccessor {
  label = input<string>('');
  options = input<{ label: string; value: any; disabled?: boolean; icon?: string }[]>([]);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  orientation = input<'horizontal' | 'vertical'>('vertical');
  size = input<ComponentSize>('md');
  errorMessage = input<string>('');
  helpText = input<string>('');
  name = input<string>('');
  class = input<string>('');

  value = signal<any>(null);
  touched = signal(false);
  labelId = `radio-group-${Math.random().toString(36).substr(2, 9)}`;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  optionId(value: any): string {
    return `${this.name() || 'radio'}-${value}`;
  }

  isSelected(value: any): boolean {
    return this.value() === value;
  }

  selectOption(value: any): void {
    this.value.set(value);
    this.onChange(value);
    this.markAsTouched();
  }

  getOptionLabelClasses(option: { disabled?: boolean }): string {
    const classes = ['flex', 'items-center', 'gap-2', 'cursor-pointer'];
    if (option.disabled || this.disabled()) {
      classes.push('opacity-50', 'cursor-not-allowed');
    }
    return classes.join(' ');
  }

  get containerClasses(): string {
    const classes = ['flex', 'gap-4'];
    if (this.orientation() === 'vertical') {
      classes.push('flex-col');
    } else {
      classes.push('flex-row', 'flex-wrap');
    }
    return classes.join(' ');
  }

  get radioClasses(): string {
    const sizeMap = {
      xs: 'w-3 h-3',
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
      xl: 'w-7 h-7'
    };

    return [
      'text-blue-600',
      'focus:ring-2',
      'focus:ring-blue-500',
      'focus:ring-offset-2',
      'transition-colors',
      'duration-200',
      sizeMap[this.size()]
    ].join(' ');
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: any): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

